"use client"

import { routeMapping } from "@/lib/route-mapping"
import { useLocation, useSearch } from "@tanstack/react-router"
import { useMemo } from "react"

export function useBreadcrumbs() {
  const { pathname } = useLocation()
  const search = useSearch({ from: "__root__" }) as { name?: string; roomNumber?: string; roomName?: string }

  const breadcrumbs = useMemo(() => {
    // Check if we have a custom mapping for this exact path
    if (routeMapping[pathname]) {
      return routeMapping[pathname]
    }

    // If no exact match, fall back to generating breadcrumbs from the path
    const segments = pathname.split("/").filter(Boolean)

    return segments.map((segment, index) => {
      const path = `/${segments.slice(0, index + 1).join("/")}`

      // Check if this segment is a house ID (previous segment is "houses")
      const isHouseIdSegment = segments[index - 1] === "houses" && !segments[index + 1]?.includes("rooms")

      // Check if this segment is a room ID (previous segment is "rooms")
      const isRoomIdSegment = segments[index - 1] === "rooms"

      // If this is a house ID segment and we have a house name, use it instead of the ID
      if (isHouseIdSegment && search?.name) {
        return {
          title: decodeURIComponent(search.name),
          link: path,
        }
      }

      // If this is a room ID segment and we have room info, use it instead of the ID
      if (isRoomIdSegment) {
        if (search?.roomName) {
          return {
            title: search.roomName,
            link: path,
          }
        }
        if (search?.roomNumber) {
          return {
            title: `Room ${search.roomNumber}`,
            link: path,
          }
        }
      }

      // For house ID in nested routes (like /houses/123/rooms)
      if (segments[index - 1] === "houses" && search?.name) {
        return {
          title: decodeURIComponent(search.name),
          link: path,
        }
      }

      // Default behavior - capitalize the segment
      return {
        title: segment.charAt(0).toUpperCase() + segment.slice(1),
        link: path,
      }
    })
  }, [pathname, search])

  return breadcrumbs
}
